import { AuditCard } from "@/features/Audit/AuditCard";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import i18n from "@/i18n";
import { useParams } from "react-router-dom";
import { Button, Link } from "@heroui/react";
import { TimeSerieFilesEditCard } from "./edit_card";
import { useCallback, useState } from "react";
import {
  FileDown,
  FileSpreadsheet,
  Sparkles,
  Database,
  RefreshCcw,
} from "lucide-react";
import { download, postAuthorized, postFormAuthorized } from "@/lib/fetch";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ActionsCard,
  type ActionsCardDownloadItem,
} from "@/components/ActionsCard";
import { Clients } from "@/lib/clients";
import { Tables } from "../../gen/tableNames/tables";

type Stage = "file" | "extracted" | "transformed" | "loaded";

const STAGES: {
  key: Stage;
  label: string;
  hint: string;
  icon: React.ElementType;
  badge?: string;
}[] = [
  {
    key: "file",
    label: "Raw file",
    hint: "Original uploaded file",
    icon: FileDown,
  },
  {
    key: "extracted",
    label: "Extracted",
    hint: "Post-extract",
    icon: FileSpreadsheet,
    badge: ".csv",
  },
  {
    key: "transformed",
    label: "Transformed",
    hint: "Normalized",
    icon: Sparkles,
    badge: ".csv",
  },
  {
    key: "loaded",
    label: "Loaded",
    hint: "Loaded to database",
    icon: Database,
    badge: ".csv",
  },
];

const STAGE_ICON_COLOR: Record<Stage, string> = {
  file: "text-stone-500",
  extracted: "text-amber-500",
  transformed: "text-violet-500",
  loaded: "text-green-600",
};

export const TimeSerieFilesCard = () => {
  const { id } = useParams<{ id: string }>();
  const baseUrl = import.meta.env.VITE_BACKEND_URL as string;

  const qc = useQueryClient();
  const numericId = Number(id);
  const itemQueryKey = [
    Clients.ETL_QUANTILIA,
    Tables.TIME_SERIE_FILES,
    "get",
    "item",
    numericId,
  ] as const;

  const [downloading, setDownloading] = useState<Stage | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [controlling, setControlling] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadFiles, setReloadFiles] = useState<File[]>([]);
  const [reloadError, setReloadError] = useState<string | null>(null);

  if (!id)
    return (
      <div className="p-8 text-danger-500">{i18n.t("errors.invalid_id")}</div>
    );

  // ---- io ----
  const buildUrl = useCallback(
    (stage: Stage) =>
      stage === "file"
        ? `${baseUrl}/${Clients.ETL_QUANTILIA}/${Tables.TIME_SERIE_FILES}/download/${id}`
        : `${baseUrl}/${Clients.ETL_QUANTILIA}/${Tables.TIME_SERIE_FILES}/download/${id}?stage=${stage}`,
    [baseUrl, id],
  );

  const onDownload = useCallback(
    async (stage: Stage) => {
      setDownloading(stage);
      try {
        await download(buildUrl(stage));
      } finally {
        setDownloading(null);
      }
    },
    [buildUrl],
  );

  const invalidateEditCard = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: itemQueryKey });
    await qc.refetchQueries({ queryKey: itemQueryKey, type: "active" });
  }, [qc, itemQueryKey]);

  const onRecompute = useCallback(async () => {
    setRecomputing(true);
    try {
      await postAuthorized(
        `${baseUrl}/${Clients.ETL_QUANTILIA}/${Tables.TIME_SERIE_FILES}/${id}/compute`,
        {},
      );
      await invalidateEditCard();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail || err?.message || "Reload failed.";
      toast.error(msg);
    } finally {
      setTimeout(() => setRecomputing(false), 250);
    }
  }, [baseUrl, id, invalidateEditCard]);

  const onControl = useCallback(async () => {
    setControlling(true);
    try {
      await postAuthorized(
        `${baseUrl}/${Clients.ETL_QUANTILIA}/${Tables.TIME_SERIE_FILES}/${id}/control`,
        {},
      );
    } finally {
      setTimeout(() => setControlling(false), 250);
    }
  }, [baseUrl, id]);

  const onReloadSubmit = useCallback(async () => {
    setReloadError(null);
    if (reloadFiles.length !== 1) {
      setReloadError("Please provide exactly one file.");
      return;
    }
    setReloading(true);
    try {
      const formData = new FormData();
      formData.append("file", reloadFiles[0]);
      await postFormAuthorized(
        `${baseUrl}/${Clients.ETL_QUANTILIA}/${Tables.TIME_SERIE_FILES}/${id}/reload`,
        formData,
      );
      setReloadFiles([]);
      await invalidateEditCard();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail || err?.message || "Reload failed.";
      setReloadError(msg);
    } finally {
      setTimeout(() => setReloading(false), 250);
    }
  }, [baseUrl, id, reloadFiles, invalidateEditCard]);

  const datesHref = toWorkspacePath(
    `/admin/qtl - etl/time_serie_file_dates?time_serie_file_id_equals=${id}`,
  );

  return (
    <div className="bg-gray-50">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-6 p-6 xl:grid-cols-3 xl:gap-10">
        {/* main content */}
        <div className="xl:col-span-2 xl:pt-2">
          <div className="mx-auto max-w-[960px]">
            <TimeSerieFilesEditCard
              id={numericId}
              client={Clients.ETL_QUANTILIA}
              table_name={Tables.TIME_SERIE_FILES}
            />
          </div>
        </div>

        {/* actions */}
        <aside className="xl:col-span-1 flex flex-col items-end gap-12 xl:pt-2">
          <Button
            href={datesHref}
            as={Link}
            variant="bordered"
            color="primary"
            radius="full"
            className="bg-white border border-neutral-300 shadow-[0_1px_0_rgba(16,24,40,0.04)]"
          >
            Time Serie File Dates
          </Button>

          <ActionsCard
            title="Actions"
            primaryActions={
              <div className="flex flex-1 gap-2">
                <Button
                  type="button"
                  onPress={onRecompute}
                  isLoading={recomputing}
                  startContent={
                    !recomputing ? <RefreshCcw className="h-4 w-4" /> : null
                  }
                  radius="lg"
                  variant="solid"
                  color="primary"
                  className="flex-1 h-10 text-sm font-medium"
                >
                  Reload
                </Button>
                <Button
                  type="button"
                  onPress={onControl}
                  isLoading={controlling}
                  radius="lg"
                  variant="bordered"
                  color="default"
                >
                  Launch control
                </Button>
              </div>
            }
            upload={{
              title: "Upload & reload file",
              subtitle: "Replace file and re-run ETL",
              files: reloadFiles,
              setFiles: setReloadFiles,
              setError: (e) => setReloadError(e ?? null),
              error: reloadError,
              onSubmit: onReloadSubmit,
              isLoading: reloading,
              maxFiles: 1,
              hideStatusChips: true,
            }}
            downloads={{
              title: "Download artifacts",
              subtitle: "Get stage outputs",
              items: STAGES.map(
                ({
                  key,
                  label,
                  hint,
                  icon: Icon,
                  badge,
                }): ActionsCardDownloadItem => ({
                  key,
                  label,
                  hint,
                  icon: Icon,
                  badge,
                  iconClassName: STAGE_ICON_COLOR[key],
                  onClick: () => onDownload(key),
                  isLoading: downloading === key,
                }),
              ),
            }}
          />
        </aside>

        {/* AUDIT pleine largeur */}
        <div className="xl:col-span-3">
          <div className="mt-6">
            <AuditCard
              id={numericId}
              client={Clients.ETL_QUANTILIA}
              audit_table_name={Tables.TIME_SERIE_FILES}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
